import { useParams, Link, Navigate } from 'react-router-dom';
import { getPost } from '../content/blog/posts';
import { lazy, Suspense, useEffect } from 'react';

// Lazy-load content components by slug
const contentModules = {
  'step-5-golden-boot-race-2025-26': lazy(() =>
    import('../content/blog/step-5-golden-boot-race-2025-26/Content')
  ),
  'nwcfl-goals-per-game-2025-26': lazy(() =>
    import('../content/blog/nwcfl-goals-per-game-2025-26/Content')
  ),
  'lewis-byrne-chadderton-53-goals': lazy(() =>
    import('../content/blog/lewis-byrne-chadderton-53-goals/Content')
  ),
};

const SKELETON_WIDTHS = ['92%', '100%', '88%', '96%', '84%', '91%', '78%', '94%'];

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (!post) return;

    const siteName = 'Below The Radar';
    const title = post.seoTitle || `${post.title} | ${siteName}`;
    const description = post.seoDescription || post.description;
    const canonicalUrl = `https://belowtheradar.co.uk/blog/${post.slug}`;
    const imageUrl = post.image
      ? new URL(post.image, 'https://belowtheradar.co.uk').toString()
      : 'https://belowtheradar.co.uk/og-image.png';

    const setMeta = (selector, attributes) => {
      let tag = document.head.querySelector(selector);

      if (!tag) {
        tag = document.createElement('meta');
        document.head.appendChild(tag);
      }

      Object.entries(attributes).forEach(([name, value]) => {
        tag.setAttribute(name, value);
      });
    };

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    document.title = title;
    canonical.setAttribute('href', canonicalUrl);
    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    setMeta('meta[property="article:published_time"]', { property: 'article:published_time', content: post.date });
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const ContentComponent = contentModules[slug];

  return (
    <article className="max-w-[680px] mx-auto antialiased">
      {/* Back link */}
      <Link
        to="/blog"
        className="inline-flex items-center gap-1.5 min-h-[40px] rounded text-[13px] font-semibold text-on-surface-variant hover:text-primary transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 mb-6"
      >
        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
        All posts
      </Link>

      {/* Article header — kicker → headline → standfirst → byline */}
      <header className="mb-8 sm:mb-10">
        <div className="flex items-center gap-2.5 mb-4">
          <span className="h-[3px] w-6 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
            {post.kicker || 'Player Watch'}
          </span>
        </div>

        <h1 className="font-headline font-expanded text-[clamp(1.875rem,5vw,2.75rem)] font-extrabold leading-[1.12] tracking-tight text-on-surface mb-4">
          {post.title}
        </h1>

        <p className="text-[1.125rem] sm:text-[1.1875rem] leading-[1.6] text-tertiary mb-6">
          {post.description}
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-on-surface-variant pb-6 border-b border-outline-variant/30">
          <span className="font-semibold text-on-surface">{post.author}</span>
          <time dateTime={post.date}>
            {new Date(post.date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </time>
          <span className="inline-flex items-center gap-1">
            <span className="material-symbols-outlined text-[15px]" aria-hidden="true">schedule</span>
            {post.readingTime} min read
          </span>
        </div>
      </header>

      {/* Hero image — outsets wider than the reading column on md+ */}
      {post.image && (
        <figure className="mb-10 md:w-[calc(100%+4rem)] md:-ml-8">
          <div className="rounded-xl overflow-hidden border border-outline-variant/30">
            <img
              src={post.image}
              alt={post.title}
              className="w-full aspect-[1200/630] object-cover"
            />
          </div>
        </figure>
      )}

      {/* Article body */}
      <div className="article-body">
        {ContentComponent ? (
          <Suspense
            fallback={
              <div className="animate-pulse space-y-6">
                {SKELETON_WIDTHS.map((width, i) => (
                  <div key={i} className="h-4 bg-surface-container-highest rounded" style={{ width }} />
                ))}
              </div>
            }
          >
            <ContentComponent />
          </Suspense>
        ) : (
          <p className="text-on-surface-variant">Content not found for this post.</p>
        )}
      </div>

      {/* Tags footer */}
      <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-outline-variant/30">
        {post.tags.map((tag) => (
          <span
            key={tag}
            className="text-[12px] font-semibold tracking-[0.06em] uppercase px-3 py-1.5 rounded-full border border-outline-variant/40 bg-surface-container text-on-surface-variant"
          >
            {tag}
          </span>
        ))}
      </div>
    </article>
  );
}
